import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Truck, CheckCircle, Mountain, Star, Palette, Droplets, Shield, ArrowRight, Phone, Calculator, Shovel } from 'lucide-react';
import SEO from '@/components/SEO';
import decorativeRockImage from '@/assets/decorative-rock-types.jpg';
import blackstarRock from '@/assets/blackstar-rock.webp';
import decomposedGranite from '@/assets/decomposed-granite.webp';
import limestoneGravel from '@/assets/limestone-gravel.webp';
import peaGravel from '@/assets/pea-gravel.webp';
import rainbowRock from '@/assets/rainbow-rock.webp';
import bullRock2Inch from '@/assets/bull-rock-2-inch.webp';
import bullRock25Inch from '@/assets/bull-rock-2-5-inch.webp';

interface Product {
  id: string;
  slug_en: string;
  slug_es: string;
  name_en: string;
  name_es: string;
  description_en: string;
  description_es: string;
  features_en: string[];
  features_es: string[];
  is_featured: boolean;
  price_book: {
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }[];
}

// Static rock varieties data
const rockVarieties = [
  {
    id: 'river-rock',
    name: 'River Rock',
    nameEs: 'Roca de Río',
    price: 45,
    image: rainbowRock,
    description: 'Smooth, naturally rounded stones perfect for decorative landscaping and drainage',
    descriptionEs: 'Piedras lisas y naturalmente redondeadas perfectas para paisajismo decorativo y drenaje',
    features: ['Natural Smooth Finish', 'Excellent Drainage', 'Low Maintenance', 'Various Sizes'],
    featuresEs: ['Acabado Natural Liso', 'Excelente Drenaje', 'Bajo Mantenimiento', 'Varios Tamaños'],
    sizes: ['3/8"', '3/4"', '1-2"', '2-4"'],
    coverage: 'Standard coverage per yard',
    applications: ['Garden beds', 'Pathways', 'Water features', 'Drainage'],
    applicationsEs: ['Lechos de jardín', 'Senderos', 'Características de agua', 'Drenaje']
  },
  {
    id: 'bull-rock',
    name: 'Bull Rock',
    nameEs: 'Bull Rock',
    price: 38,
    image: bullRock2Inch,
    description: 'Angular limestone rock ideal for drainage and erosion control applications',
    descriptionEs: 'Roca caliza angular ideal para aplicaciones de drenaje y control de erosión',
    features: ['Angular Shape', 'Excellent Drainage', 'Erosion Control', 'Durable'],
    featuresEs: ['Forma Angular', 'Excelente Drenaje', 'Control de Erosión', 'Duradero'],
    sizes: ['3/4"', '1-2"', '2-4"', '4-8"'],
    coverage: 'Standard coverage per yard',
    applications: ['French drains', 'Erosion control', 'Road base', 'Retaining walls'],
    applicationsEs: ['Drenajes franceses', 'Control erosión', 'Base de carretera', 'Muros contención']
  },
  {
    id: 'pea-gravel',
    name: 'Pea Gravel',
    nameEs: 'Grava de Guisante',
    price: 42,
    image: peaGravel,
    description: 'Small, rounded stones perfect for walkways, driveways, and decorative areas',
    descriptionEs: 'Piedras pequeñas y redondeadas perfectas para senderos, entradas y áreas decorativas',
    features: ['Small Size', 'Easy Installation', 'Walkable Surface', 'Natural Colors'],
    featuresEs: ['Tamaño Pequeño', 'Instalación Fácil', 'Superficie Transitable', 'Colores Naturales'],
    sizes: ['3/8"'],
    coverage: 'Standard coverage per yard',
    applications: ['Walkways', 'Patios', 'Playgrounds', 'Garden borders'],
    applicationsEs: ['Senderos', 'Patios', 'Parques', 'Bordes jardín']
  },
  {
    id: 'limestone',
    name: 'Crushed Limestone',
    nameEs: 'Piedra Caliza Triturada',
    price: 35,
    image: limestoneGravel,
    description: 'Versatile crushed limestone for road base, drainage, and construction projects',
    descriptionEs: 'Piedra caliza triturada versátil para base de carretera, drenaje y proyectos de construcción',
    features: ['Compactable', 'Structural Strength', 'Cost Effective', 'Multiple Uses'],
    featuresEs: ['Compactable', 'Fuerza Estructural', 'Económico', 'Múltiples Usos'],
    sizes: ['3/4"', '1-2"', 'Base'],
    coverage: 'Standard coverage per yard',
    applications: ['Road base', 'Driveways', 'Construction', 'Pipe bedding'],
    applicationsEs: ['Base carretera', 'Entradas', 'Construcción', 'Base tuberías']
  },
  {
    id: 'decomposed-granite',
    name: 'Decomposed Granite',
    nameEs: 'Granito Descompuesto',
    price: 40,
    image: decomposedGranite,
    description: 'Natural pathway material that compacts well and provides excellent drainage',
    descriptionEs: 'Material natural para senderos que se compacta bien y proporciona excelente drenaje',
    features: ['Natural Compaction', 'Permeable Surface', 'Earth Tones', 'Stabilized'],
    featuresEs: ['Compactación Natural', 'Superficie Permeable', 'Tonos Tierra', 'Estabilizado'],
    sizes: ['1/4"'],
    coverage: 'Standard coverage per yard',
    applications: ['Pathways', 'Patios', 'Courtyards', 'Xeriscape'],
    applicationsEs: ['Senderos', 'Patios', 'Patios interiores', 'Xeriscaping']
  },
];

const RockPage = () => {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRockProducts = async () => {
      try {
        const { data: category } = await supabase
          .from('categories')
          .select('id')
          .eq(`slug_${language}`, language === 'en' ? 'rock' : 'roca')
          .single();

        if (category) {
          const { data: rockProducts } = await supabase
            .from('products')
            .select(`
              *,
              price_book (
                list_price,
                sale_price,
                unit_label_en,
                unit_label_es
              )
            `)
            .eq('category_id', category.id)
            .eq('is_active', true)
            .order('sort_order');

          setProducts(rockProducts || []);
        }
      } catch (error) {
        console.error('Error fetching rock products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRockProducts();
  }, [language]);

  const getProductName = (product: Product) => 
    language === 'en' ? product.name_en : product.name_es;
  
  const getProductDescription = (product: Product) => 
    language === 'en' ? product.description_en : product.description_es;
  
  const getProductFeatures = (product: Product) => 
    language === 'en' ? product.features_en : product.features_es;

  const getUnitLabel = (product: Product) => 
    language === 'en' ? product.price_book[0]?.unit_label_en : product.price_book[0]?.unit_label_es;

  const seoTitle = language === 'en' 
    ? 'Decorative Rock & Stone Delivery Texas | River Rock & Bull Rock'
    : 'Entrega de Roca Decorativa Texas | River Rock & Bull Rock';

  const seoDescription = language === 'en'
    ? 'Quality decorative rock, river rock, bull rock delivery in Montgomery County TX. Perfect for landscaping and drainage. Professional delivery service.'
    : 'Entrega de roca decorativa, roca de río, bull rock en el Condado Montgomery TX. Perfecta para jardinería y drenaje. Servicio de entrega profesional.';
  
  const seoKeywords = language === 'en'
    ? 'decorative rock delivery texas, river rock montgomery county, bull rock conroe, landscape rock texas, drainage rock woodlands, gravel delivery texas'
    : 'entrega roca decorativa texas, river rock montgomery county, bull rock conroe, roca paisaje texas, roca drenaje woodlands, entrega grava texas';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": seoTitle,
    "description": seoDescription,
    "category": "Landscaping Supplies",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/rock' : '/es/roca'}
        structuredData={structuredData}
        language={language}
      />
      <Header />
      <MobileCTA />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent animate-pulse-glow"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="animate-fade-in">
            <div className="flex items-center justify-center mb-6">
              <Mountain className="h-12 w-12 text-primary-foreground mr-4 animate-float" />
              <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground">
                {language === 'en' ? 'Decorative Rock & Stone' : 'Roca Decorativa y Piedra'}
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-primary-foreground/90 mb-8 max-w-4xl mx-auto leading-relaxed">
              {language === 'en' 
                ? 'Premium decorative rock and stone materials for landscaping, drainage, and aesthetic appeal throughout Montgomery County, Texas.'
                : 'Materiales de roca decorativa y piedra premium para jardinería, drenaje y atractivo estético en todo el Condado Montgomery, Texas.'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Mountain className="h-5 w-5 mr-2" />
                {language === 'en' ? '🏔️ Natural Stone Quality' : '🏔️ Calidad de Piedra Natural'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Droplets className="h-5 w-5 mr-2" />
                {language === 'en' ? '💧 Drainage Solutions' : '💧 Soluciones de Drenaje'}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/${language}/quote`}>
                <Button variant="hero" size="lg" className="hover-scale">
                  {language === 'en' ? 'Get Quote Now' : 'Obtener Cotización Ahora'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:9363200763">
                <Button variant="outline" size="lg" className="border-white text-green-700 hover:bg-white hover:text-foreground">
                  <Phone className="mr-2 h-5 w-5" />
                  {language === 'en' ? 'Call Expert' : 'Llamar Experto'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Rock Selection Guide */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Choose Your Perfect Rock' : 'Elija su Roca Perfecta'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Each rock type is specially selected for specific landscaping and functional applications. Find the perfect match for your project needs.'
                : 'Cada tipo de roca está especialmente seleccionado para aplicaciones específicas de paisajismo y funcionales. Encuentre la combinación perfecta para las necesidades de su proyecto.'
              }
            </p>
          </div>

          <Tabs defaultValue="grid" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-12">
              <TabsTrigger value="grid">
                {language === 'en' ? 'View All' : 'Ver Todos'}
              </TabsTrigger>
              <TabsTrigger value="calculator">
                {language === 'en' ? 'Calculator' : 'Calculadora'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rockVarieties.map((rock, index) => (
                  <Card 
                    key={rock.id} 
                    className="group hover:shadow-glow transition-all duration-500 border-2 hover:border-primary overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={rock.image} 
                        alt={language === 'en' ? rock.name : rock.nameEs}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-1 shadow-lg">
                        <span className="font-bold text-primary">
                          ${rock.price}/yard
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {language === 'en' ? rock.name : rock.nameEs}
                        </h3>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4 text-sm">
                        {language === 'en' ? rock.description : rock.descriptionEs}
                      </p>
                      
                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {(language === 'en' ? rock.features : rock.featuresEs).slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Specs */}
                      <div className="space-y-2 mb-6 text-xs bg-muted/30 p-3 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Sizes:' : 'Tamaños:'}
                          </span>
                          <span className="font-medium">{rock.sizes.join(', ')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'en' ? 'Coverage:' : 'Cobertura:'}
                          </span>
                          <span className="font-medium text-right">{rock.coverage}</span>
                        </div>
                      </div>

                      {/* Applications */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-foreground mb-2 text-sm">
                          {language === 'en' ? 'Best For:' : 'Mejor Para:'}
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {(language === 'en' ? rock.applications : rock.applicationsEs).slice(0, 3).map((app, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {app}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Link to={`/${language}/quote`} className="flex-1">
                          <Button variant="hero" className="w-full group-hover:scale-105 transition-transform">
                            {language === 'en' ? 'Add to Quote' : 'Agregar a Cotización'}
                          </Button>
                        </Link>
                        <Button variant="outline" size="icon" className="hover-scale">
                          <Mountain className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="calculator">
              <Card className="max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-6 w-6 text-primary" />
                    {language === 'en' ? 'Rock Coverage Calculator' : 'Calculadora de Cobertura de Roca'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-primary/5 p-6 rounded-lg">
                    <h4 className="font-semibold mb-4">
                      {language === 'en' ? 'Quick Coverage Guide:' : 'Guía Rápida de Cobertura:'}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span>{language === 'en' ? '2" depth coverage:' : 'Cobertura 2" profundidad:'}</span>
                        <span className="font-semibold">Standard coverage</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>{language === 'en' ? '3" depth coverage:' : 'Cobertura 3" profundidad:'}</span>
                        <span className="font-semibold">Thick coverage</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>{language === 'en' ? 'Drainage applications:' : 'Aplicaciones drenaje:'}</span>
                        <span className="font-semibold">4-6" depth recommended</span>
                      </div>
                    </div>
                    <div className="mt-6 p-4 bg-background rounded border">
                      <p className="text-sm text-muted-foreground">
                        {language === 'en'
                          ? '💡 Pro Tip: For drainage projects, use angular rocks like Bull Rock. For decorative applications, River Rock provides a more polished look.'
                          : '💡 Consejo Profesional: Para proyectos de drenaje, use rocas angulares como Bull Rock. Para aplicaciones decorativas, River Rock proporciona una apariencia más pulida.'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Rock Applications & Benefits' : 'Aplicaciones y Beneficios de Roca'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Discover the versatile applications of decorative rock in modern landscaping and construction projects'
                : 'Descubra las aplicaciones versátiles de la roca decorativa en proyectos modernos de paisajismo y construcción'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Droplets,
                title: language === 'en' ? 'Drainage Solutions' : 'Soluciones de Drenaje',
                description: language === 'en' 
                  ? 'Excellent for French drains, dry creek beds, and water management around foundations. Angular rocks provide superior drainage.'
                  : 'Excelente para drenajes franceses, lechos de arroyos secos y manejo de agua alrededor de cimientos. Las rocas angulares proporcionan drenaje superior.',
                applications: ['French drains', 'Foundation drainage', 'Dry creek beds']
              },
              {
                icon: Palette,
                title: language === 'en' ? 'Decorative Landscaping' : 'Jardinería Decorativa',
                description: language === 'en'
                  ? 'Perfect for garden beds, walkways, and accent features that enhance curb appeal and property value.'
                  : 'Perfecto para lechos de jardín, senderos y características de acento que mejoran el atractivo exterior y el valor de la propiedad.',
                applications: ['Garden beds', 'Pathways', 'Water features']
              },
              {
                icon: Shield,
                title: language === 'en' ? 'Erosion Control' : 'Control de Erosión',
                description: language === 'en'
                  ? 'Heavy rock materials effectively prevent soil erosion on slopes and high-traffic areas.'
                  : 'Los materiales de roca pesada previenen efectivamente la erosión del suelo en pendientes y áreas de alto tráfico.',
                applications: ['Slope stabilization', 'Retaining walls', 'Shoreline protection']
              },
              {
                icon: Mountain,
                title: language === 'en' ? 'Low Maintenance' : 'Bajo Mantenimiento',
                description: language === 'en'
                  ? 'Long-lasting solution that provides immediate visual impact with minimal ongoing maintenance requirements.'
                  : 'Solución duradera que proporciona impacto visual inmediato con requisitos mínimos de mantenimiento continuo.',
                applications: ['Commercial projects', 'Residential landscapes', 'Public spaces']
              }
            ].map((benefit, index) => (
              <Card key={index} className="hover:shadow-glow transition-all duration-300 group">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <benefit.icon className="h-8 w-8 text-primary group-hover:animate-float" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div>
                    <h4 className="font-semibold text-foreground text-xs mb-2">
                      {language === 'en' ? 'Common Uses:' : 'Usos Comunes:'}
                    </h4>
                    <div className="space-y-1">
                      {benefit.applications.map((app, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                          <span className="text-xs text-muted-foreground">{app}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Installation Guide */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {language === 'en' ? 'Professional Installation Guide' : 'Guía de Instalación Profesional'}
              </h2>
              <p className="text-lg text-muted-foreground">
                {language === 'en'
                  ? 'Expert guidance for perfect rock installation and long-lasting results'
                  : 'Orientación experta para instalación perfecta de roca y resultados duraderos'
                }
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Site Preparation */}
              <Card className="hover:shadow-glow transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Shovel className="h-6 w-6 text-primary" />
                    {language === 'en' ? 'Site Preparation' : 'Preparación del Sitio'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      language === 'en' ? 'Clear area of vegetation and debris' : 'Limpiar el área de vegetación y escombros',
                      language === 'en' ? 'Install landscape fabric for weed control' : 'Instalar tela de paisaje para control de malezas',
                      language === 'en' ? 'Grade area for proper drainage slope' : 'Nivelar área para pendiente de drenaje adecuada',
                      language === 'en' ? 'Compact soil base for stability' : 'Compactar base de suelo para estabilidad'
                    ].map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-sm text-muted-foreground">{step}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Installation Process */}
              <Card className="hover:shadow-glow transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Mountain className="h-6 w-6 text-primary" />
                    {language === 'en' ? 'Installation Process' : 'Proceso de Instalación'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      language === 'en' ? 'Apply rock 2-3 inches deep for best coverage' : 'Aplicar roca de 2-3 pulgadas de profundidad para mejor cobertura',
                      language === 'en' ? 'Use edging to contain rock in designated areas' : 'Usar bordillos para contener la roca en áreas designadas',
                      language === 'en' ? 'Level and distribute evenly with rake' : 'Nivelar y distribuir uniformemente con rastrillo',
                      language === 'en' ? 'Water lightly to settle and remove dust' : 'Regar ligeramente para asentar y remover polvo'
                    ].map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-sm text-muted-foreground">{step}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pro Tips */}
            <Card className="mt-8 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl text-center text-foreground">
                  {language === 'en' ? 'Professional Tips' : 'Consejos Profesionales'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      {language === 'en' ? 'Drainage Projects' : 'Proyectos de Drenaje'}
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• {language === 'en' ? 'Use angular rocks (Bull Rock) for better interlocking' : 'Use rocas angulares (Bull Rock) para mejor entrelazado'}</li>
                      <li>• {language === 'en' ? 'Install 4-6 inches deep for French drains' : 'Instalar 4-6 pulgadas de profundidad para drenajes franceses'}</li>
                      <li>• {language === 'en' ? 'Wrap with filter fabric to prevent clogging' : 'Envolver con tela filtrante para prevenir obstrucción'}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      {language === 'en' ? 'Decorative Applications' : 'Aplicaciones Decorativas'}
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• {language === 'en' ? 'Use smooth rocks (River Rock) for aesthetic appeal' : 'Use rocas lisas (River Rock) para atractivo estético'}</li>
                      <li>• {language === 'en' ? 'Apply 2-3 inches deep for ground cover' : 'Aplicar 2-3 pulgadas de profundidad para cobertura del suelo'}</li>
                      <li>• {language === 'en' ? 'Create borders with edging for clean lines' : 'Crear bordes con bordillos para líneas limpias'}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Ready for Your Rock Project?' 
              : '¿Listo para su Proyecto de Roca?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Contact our rock and stone experts today for personalized recommendations and professional delivery services. We provide quality materials and reliable service for every project.'
              : 'Contacte a nuestros expertos en roca y piedra hoy para recomendaciones personalizadas y servicios de entrega profesional. Proporcionamos materiales de calidad y servicio confiable para cada proyecto.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/quote`}>
              <Button variant="hero" size="lg" className="hover-scale">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 text-green-700 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Call Expert' : 'Llamar Experto'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default RockPage;